import React, { ReactNode } from 'react';
import cn from 'classnames';
import './UpdateHelpModal.scss';
import { StateBar, StateColor, StateIcon } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { UpdateState } from 'enums';
import { useTranslation } from 'react-i18next';

interface IUpdateHelpModalProps {
    show: boolean,
    onHide: () => void,
}

export function UpdateHelpModal({ show, onHide }: IUpdateHelpModalProps) {
    const { t } = useTranslation();

    const items: {state: UpdateState, content: ReactNode}[] = [
        {state: UpdateState.Draft, content: <>
            This is where you can edit an update.
            Choose a manufacturer and model, then add filters to target some devices.
            When you're ready, submit the update.
            <ul>
                <li>
                    Create stages to schedule a gradual rollout.
                </li>
                <li>
                    For example, you might deploy to 1 location, then 3, then 10.
                </li>
                <li>
                    You can use the "Devices per Location" filter to target a subset of the devices at each location,
                    which is safer to start with, in case the update makes the devices inoperable.
                </li>
            </ul>
        </>},
        {state: UpdateState.AwaitApproval, content: <>
            Every update must be approved before it can be deployed. Certain users are marked as "Approvers",
            who are responsible for reviewing updates. Once submitted, an update can't be edited.
            <ul>
                <li>Only approvers in the update's Nofications list will be notified when it's ready for review.</li>
            </ul>
        </>},
        {state: UpdateState.Scheduled, content: <>
            The update has been approved. Changes can be made by reverting it to a draft before it starts.
        </>},
        {state: UpdateState.Running, content: <>
            The update is being sent to devices.
            <ul>
                <li>The update is immediately sent to all targeted devices in the stage.</li>
                <li>At periodic intervals, the update is resent if a device hasn't responded yet.</li>
                <li>
                    A device is considered updated when it reports that it's running the new version.
                    Many devices don't apply updates until after they power cycle to avoid interrupting cooking.
                </li>
                <li>
                    A running update can't be reverted to a draft, but you can still adjust the start/end time of scheduled stages.
                </li>
            </ul>
        </>},
        {state: UpdateState.Done, content: <>
            A stage will complete when there aren't any devices left to update, or when it reaches its end time.
            The update will complete when all stages have finished.
        </>},
    ];

    const updateClasses = {
        [UpdateState.Draft]: 'updateState_draft',
        [UpdateState.AwaitApproval]: 'updateState_awaitApproval',
        [UpdateState.Scheduled]: 'updateState_scheduled',
        [UpdateState.Running]: 'updateState_running',
        [UpdateState.Done]: 'updateState_done',
        [UpdateState.Canceled]: 'updateState_canceled',
        [UpdateState.Error]: 'updateState_error',
    }

    return (
        <Modal show={show} onClose={onHide} size={'lg'} scrollMode='modal-body'>
            <ModalHeader>
                Update Life Cycle
            </ModalHeader>
            <ModalBody style={{textAlign: "left"}}>
                <div className="updateHelp_timeline">
                    {items.map((item, i) => (
                        <div className={cn('updateHelp_timelineItem', updateClasses[item.state])} key={i}>
                            <div className="updateHelp_timelineBarSection">
                                <div className="updateHelp_timelineBar">
                                    {item.state === UpdateState.Running && (
                                        <StateBar state={UpdateState.Running} />
                                    )}
                                </div>
                                <div className="updateHelp_timelineCircleOuter">
                                    <div className="updateHelp_timelineCircleInner"></div>
                                </div>
                            </div>
                            <div className="updateHelp_timelineContentSection">
                                <h2 className="updateHelp_timelineTitle">
                                    <StateIcon state={item.state} />
                                    <StateColor state={item.state}>
                                        {t('updateState.' + item.state)}
                                    </StateColor>
                                </h2>
                                <div className="updateHelp_timelineContent">
                                    {item.content}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onClick={onHide}>
                    Ok
                </Button>
            </ModalFooter>
        </Modal>
    );
}